import { MuiTypography } from "@/components/ui/mui-typography";
import { fieldText, type CatalogListItem } from "@/lib/catalog-item";
import { cn } from "@/lib/utils";

// The labelled facts the card and the list row both print, in the donor's
// wording and the donor's order.
//
// One module because the *set* is the parity contract, not the layout: card and
// row show the same facts in the same order, and a fifth added to one but not
// the other is exactly the drift a shared list prevents.
//
// Two things here are the donor's decisions rather than this file's, and both
// were got wrong on the first pass:
//
//   - **The label is spelled out and carries a colon** — `Category: Bath`,
//     `JDE Item Number: 12345`. `item-card.tsx` and `item-data-row.tsx` print
//     `Category: `, `Model Number: `, `JDE Item Number: `, `Finish: `. The
//     abbreviations this port had invented ("Model #", "JDE #") are the table's
//     column headings, not the card's labels.
//   - **Two of the four disappear when empty; two print `N/A`.** The donor
//     guards Category and Finish behind `{displayCategory && ...}` and prints
//     `{displayModelNumber || 'N/A'}` unguarded. So a card's field block is not
//     a fixed four rows — it is two rows plus whichever of the other two the
//     item has.
//
// The whole line is one run of secondary text (`Typography body2
// color='text.secondary'`), label and value alike — there is no emphasis on
// the value.

/**
 * The card/row fields, in render order.
 *
 * Module-private: the *set* is what card and row share, and they share it by
 * both rendering `ItemFields` rather than by each mapping over the list. A view
 * that needed to iterate it separately would be a view drifting away from the
 * other two.
 */
const ITEM_FIELDS = [
  { label: "Category", read: (item: CatalogListItem) => item.category?.name, omitWhenEmpty: true },
  {
    label: "Model Number",
    read: (item: CatalogListItem) => item.modelNumber,
    omitWhenEmpty: false,
  },
  {
    label: "JDE Item Number",
    read: (item: CatalogListItem) => item.jdeItemNumber,
    omitWhenEmpty: false,
  },
  { label: "Finish", read: (item: CatalogListItem) => item.finish?.name, omitWhenEmpty: true },
] as const;

interface ItemFieldProps {
  label: string;
  value: string | null | undefined;
  className?: string;
}

/**
 * One labelled fact — `Label: value`, all of it secondary text.
 *
 * It **wraps**: the donor's is a bare `Typography variant='body2'`
 * (`item-card.tsx:227-243`) with no `noWrap` and no `textOverflow`, so a long
 * value takes a second line and the card grows. This carried `truncate`, which
 * at the four-column track #528 restored turned `JDE Item Number:
 * XAPPFFRM0001234567` into `JDE Item Number: XAPPFFRM0…` — an identifier with
 * its tail cut off, which is the data going missing rather than a layout
 * preference.
 */
export function ItemField({ label, value, className }: ItemFieldProps) {
  return (
    <MuiTypography
      data-testid={`item-field-${label}`}
      variant="body2"
      color="secondary"
      className={cn("min-w-0", className)}
    >
      {label}: {fieldText(value)}
    </MuiTypography>
  );
}

/** The block of fields this item actually has, for a caller that wants it whole. */
export function ItemFields({ item, className }: { item: CatalogListItem; className?: string }) {
  return (
    <div className={className}>
      {ITEM_FIELDS.filter((field) => !field.omitWhenEmpty || field.read(item)?.trim()).map(
        (field) => (
          <ItemField key={field.label} label={field.label} value={field.read(item)} />
        ),
      )}
    </div>
  );
}
